import styled from "@emotion/styled";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Typography,
} from "@mui/material";
import { FC, Fragment, useRef, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { IModule } from "../../../../../interfaces/IFurniture";
import { Add, ArrowDropDown, ArrowDropUp, Delete, Edit } from "@mui/icons-material";
import { useModal } from "../../../../../hooks/useModal";
import AddModulesForm from "../../../../../components/forms/furniture/AddModulesForm/AddModulesForm";
import furnitureController from "../../../../../Api/furnitureController";
import { useToast } from "../../../../../hooks/useToast";
import { useConfirmDialog } from "../../../../../hooks/useConfirmDialog";
import EditModuleForm from "../../../../../components/forms/furniture/EditModuleForm";
import EditModuleImageForm from "../../../../../components/forms/furniture/EditModuleImageForm";
import DimensionHelper from "../../../../../helpers/dimension.helper";
import { baseURL } from "../../../../../config";

const BASE_URL = baseURL;

const LeftCard = styled(Accordion)`
  width: 600px;
  max-width: calc(100% - 40px);
  padding: 20px;

  @media screen and (max-width: 940px) {
    width: 100%;
    max-width: 100%;
    padding: 0;
  }
`;

interface ModulesComponentProps {
    modules?: IModule[] | null;
    showEditTools?: boolean;
    furnitureId: string;
    onSetModules: (modules: IModule[]) => void;
}

const ModulesComponent: FC<ModulesComponentProps> = ({
    modules,
    showEditTools,
    furnitureId,
    onSetModules,
}) => {
    const [open, setOpen] = useState(false);
    const modal = useModal();
    const toast = useRef(useToast()).current;
    const { confirm, dialog } = useConfirmDialog();


    const handleDeleteModuleWithConfirm = async (moduleId: string) => {
        const result = await confirm("Czy na pewno chcesz usunąć ten moduł?");
        if (result) {
            handleDeleteModule(moduleId);
        }
    };


    const handleDeleteModule = async (moduleId: string) => {
        const res = await furnitureController.removeModule(furnitureId, moduleId)
        if (!res.ok) return toast.error('Nie udało się usunąć modułu')
        onSetModules(res.editedFurniture?.modules || [])
    }


    const handleMoveModule = async (moduleId: string, direction: "up" | "down") => {
        const res = await furnitureController.moveModule(furnitureId, moduleId, direction);

        if (!res.ok) {
            toast.error('Nie udało się przesunąć modułu');
            return;
        }
        onSetModules(res.editedFurniture?.modules || []);
    };


    const handleOpenEditForm = async (moduleData: IModule) => {
        modal.open(
            <EditModuleForm
                moduleData={moduleData}
                moduleId={moduleData._id}
                furnitureId={furnitureId}
                onSetModules={onSetModules}
            />
        )
    }


    const handleOpenEditImageForm = async (module: IModule) => {
        modal.open(
            <EditModuleImageForm
                furnitureId={furnitureId}
                moduleId={module._id}
                onSetModules={onSetModules}
            />
        )
    }


    if (!modules || modules.length === 0)
        return showEditTools ? (
            <LeftCard
                sx={{
                    mt: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    "&:before": {
                        display: "none",
                    },
                }}
            >
                <Button
                    variant="outlined"
                    startIcon={<Add />}
                    color="success"
                    onClick={() =>
                        modal.open(<AddModulesForm furnitureId={furnitureId} />)
                    }
                >
                    Dodaj moduły
                </Button>
            </LeftCard>
        ) : (
            <></>
        );

    return (
        <>
            {dialog}
            <LeftCard
                sx={{
                    mt: 2,
                    "&:before": {
                        display: "none",
                    },
                }}
            >
                <Accordion expanded={open} sx={{ boxShadow: "none" }}>
                    <AccordionSummary
                        onClick={() => setOpen(!open)}
                        sx={{
                            "@media (min-width: 940px)": {
                                px: 0,
                            },
                        }}
                        expandIcon={<ExpandMoreIcon />}
                    >
                        <Typography variant="h6" >
                            Moduły
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails
                        sx={{
                            display: "flex",
                            padding: 0,
                            userSelect: "none",
                        }}
                    >
                        <List dense sx={{ width: "100%", bgcolor: "background.paper" }}>
                            {modules?.map((module, index) => (
                                <Box key={index}>
                                    <ListItem disablePadding>
                                        <ListItem>
                                            {showEditTools && (
                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ m: 0 }}
                                                        onClick={() => handleMoveModule(module._id, "up")}
                                                    >
                                                        <ArrowDropUp />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ m: 0 }}
                                                        color="error"
                                                        onClick={() => handleDeleteModuleWithConfirm(module._id)}
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ m: 0 }}
                                                        color="secondary"
                                                        onClick={() => handleOpenEditForm(module)}
                                                    >
                                                        <Edit />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ m: 0 }}
                                                        onClick={() => handleMoveModule(module._id, "down")}
                                                    >
                                                        <ArrowDropDown />
                                                    </IconButton>
                                                </Box>
                                            )}
                                            <ListItemAvatar>
                                                <Box sx={{ position: "relative", width: 100, height: 100, marginRight: "10px" }}>
                                                    <img
                                                        alt={module.name}
                                                        style={{
                                                            width: 100,
                                                            height: 100,
                                                            objectFit: "contain",
                                                        }}
                                                        src={BASE_URL + module.image}
                                                    />
                                                    {showEditTools && (
                                                        <>
                                                            <IconButton
                                                                sx={{
                                                                    position: "absolute",
                                                                    top: "50%",
                                                                    left: "50%",
                                                                    transform: "translate(-50%, -50%)",
                                                                    zIndex: 2,
                                                                }}
                                                                size="small"
                                                                onClick={() => handleOpenEditImageForm(module)}
                                                            >
                                                                <Edit />
                                                            </IconButton>
                                                        </>
                                                    )}
                                                </Box>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={module.name}
                                                secondary={
                                                    <Fragment>
                                                        {DimensionHelper.formatDimensions(module.width, module.depth, module.height)}
                                                        <p style={{ fontSize: "13px", margin: 0, whiteSpace: "pre-line" }}>
                                                            {module.description}
                                                        </p>
                                                        {showEditTools && <p style={{ fontSize: "13px", margin: 0, whiteSpace: "pre-line" }}>
                                                            {module.moduleType === 'left' ? 'Lewy' :
                                                                module.moduleType === 'right' ? 'Prawy' :
                                                                    module.moduleType === 'middle' ? 'Środkowy' :
                                                                        module.moduleType === 'corner' ? 'Narożnik' :
                                                                            module.moduleType === 'solo' ? 'Wolnostojący' : ''}
                                                        </p>}
                                                    </Fragment>
                                                }
                                            />
                                        </ListItem>
                                    </ListItem>
                                    <Divider sx={{ m: 1 }} />
                                </Box>
                            ))}
                            {showEditTools && (
                                <Box
                                    sx={{
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        startIcon={<Add />}
                                        color="success"
                                        onClick={() =>
                                            modal.open(<AddModulesForm furnitureId={furnitureId} />)
                                        }
                                    >
                                        Dodaj moduły
                                    </Button>
                                </Box>
                            )}
                        </List>
                    </AccordionDetails>
                </Accordion>
            </LeftCard>
        </>
    );
};

export default ModulesComponent;
